var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ProductComplianceLabelTypeEnum, ProductComplianceTechnicalHeadingTypeEnum, ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { ComponentElementException } from '@inter-ikea-kompis/exceptions';
import { DexfSettingsUtility, PriceUtility, ProductUtility, TotalPriceCalculator } from '@inter-ikea-kompis/utilities';
import { EcoFee } from '@inter-ikea-kompis/component-eco-fee';
import { ChileSecLinkModalEnum, EnergyEfficiencyClassModalEnum, EprelLink, EprelLinkModalEnum, SecLink } from '@inter-ikea-kompis/component-energy-efficiency-class';
import { EnergyEfficiencyClass } from '@inter-ikea-kompis/component-energy-efficiency-class';
import { PriceFormatter } from '@inter-ikea-kompis/component-price-formatter';
import { RepairabilityIndex } from '@inter-ikea-kompis/component-repairability-index';
import { Text } from '@inter-ikea-kompis/component-text';
import { WaterEfficiencyClassModalEnum } from '@inter-ikea-kompis/component-water-efficiency-class';
import { WaterEfficiencyClass } from '@inter-ikea-kompis/component-water-efficiency-class';
import { CommercialMessage as SkapaCommercialMessage } from '@ingka/commercial-message-webc/passive.js';
import * as SkapaStyle from '@ingka/common-styles-webc';
import PriceModuleLayoutTypeEnum from '../../enums/PriceModuleLayoutTypeEnum.js';
import PriceModuleModalEnum from '../../enums/PriceModuleModalEnum.js';
import PriceModuleProductLinkEnum from '../../enums/PriceModuleProductLinkEnum.js';
import PriceModuleUtility from '../../utilities/PriceModuleUtility.js';
import PriceModuleStyle from './PriceModuleStyle.js';
/**
 * Price module component.
 *
 * @event productNameClick Triggered when product name is clicked
 * @event modalClose Triggered when the modal has been closed
 * @event modalOpen Triggered when a modal has been opened. event.detail.modalType tells you the type
 */
export default class PriceModule extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Layout
        this.layout = PriceModuleLayoutTypeEnum.default;
        // Name of the product of product set
        this.productName = null;
        // Product name text underline effect
        this.underline = false;
        // Set price to be animated
        this.animated = false;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Disables focus style. Should be used if a parent element is holding the focus style for the price module.
        this.disableFocusStyle = false;
        // Show eco fee
        this.showEcoFee = false;
        // Show price unit.
        this.showPriceUnit = false;
        // Show energy and water compliance labels.
        this.showEnergyLabel = false;
        // Show repairability index.
        this.showRepairabilityIndex = false;
        // Show price excl tax.
        this.showPriceExclTax = false;
        // Show a modal.
        this.visibleModal = null;
        // Recommended pattern for normal and kiosk mode, is eventOnly to open a ProductInformationCard in a Skapa sheet modal. If using productInformationPage, disable on kiosk.
        this.productLink = PriceModuleProductLinkEnum.disabled;
        // If true will display the price indicator 'From' price which is the lowest main/regular price for that product, not including all price/legal/business rules and should only be used for auto adapt products.
        this.isFromPrice = false;
        // Applicable when shoppingProducts is more than 1. If true, the products will be treated as one product. If one product has a new lower price then the whole product will as well.
        this.isCombinedProduct = false;
        // Show lowest price in 30 days for reduced prices. Only for single items. If there are several shopping items available an error will be thrown.
        this.showLowestPreviousSalesPrice = false;
        // Heading level for the top heading in the component. In this case there is only one heading, the product name. The default value is 3. If you don't want semantic headings on the page, use the disableHeadings property.
        this.startHeadingsAtLevel = 3;
        // Disable heading elements and use p tags instead. Used when the price module is not the primary heading for a better screen reader experience. To set heading levels, use the startHeadingsAtLevel property.
        this.disableHeadings = false;
        this.dexfSettings = null;
        this.getPricesFromTotalPrice = (totalPrice, text) => {
            return html `
			<div class="regularPriceTextContainer text--body-s">
				<span class="regularPricePrefixText" aria-hidden="true">${text}:&nbsp;</span>

				${totalPrice.map((price, index) => {
                const lastPriceToShow = index === totalPrice.length - 1;
                return html `
						<kompis-price-formatter
							class="regularPriceText"
							.localizedInformation="${this.localizedInformation}"
							.settings="${this.settings}"
							.amount="${price.regularInclTax}"
							.ariaLabelPrice="${text}"
							.currencySymbol="${PriceUtility.getCurrencySymbol(price, this.getSettings())}"
						>
						</kompis-price-formatter>
						${this.getPriceUnit({ priceUnitLayout: 'subtle' })}
						${lastPriceToShow ? null : html `<span>/</span>`}
					`;
            })}
			</div>
		`;
        };
    }
    /**
     * Renders view.
     *
     * @returns Template.
     */
    render() {
        if (!this.shoppingProducts || !this.hasTranslationsAndSettings()) {
            return null;
        }
        const totalPrice = TotalPriceCalculator.calculateMultiple(this.shoppingProducts, this.getSettings().localisation.dateFormat, this.isCombinedProduct);
        const primaryCurrency = totalPrice[0];
        const loyaltyProgramEqualFontSize = this.getSettings().kompis.priceSettings.loyaltyProgramEqualFontSize;
        // Hacky solution used to remove margin-bottom from commercial message when not needed.
        const isComponentListTotalPriceChild = this.parentElement?.className === 'priceDisplay';
        const styleOptions = {
            theme: this.theme,
            isBti: primaryCurrency && PriceUtility.isBreathTakingItem(primaryCurrency),
            layout: this.layout,
            productLink: this.productLink,
            disableLinks: this.disableLinks,
            underline: this.underline,
            complianceType: ProductUtility.getProductComplianceType(this.shoppingProducts[0]?.product),
            loyaltyProgramEqualFontSize,
            withoutPrice: !primaryCurrency,
            disableFocusStyle: this.disableFocusStyle,
            isComponentListTotalPriceChild
        };
        return html `
			<style>
				${PriceModuleStyle.getStyle(styleOptions)}
			</style>

			${!primaryCurrency
            ? this.getPriceModuleWithoutPrice()
            : this.isFromPrice
                ? this.getFromPrice(totalPrice)
                : this.getPriceModule(totalPrice)}
		`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-commercial-message', SkapaCommercialMessage);
        this.customElements.define('kompis-price-formatter', PriceFormatter);
        this.customElements.define('kompis-eco-fee', EcoFee);
        this.customElements.define('kompis-repairability-index', RepairabilityIndex);
        this.customElements.define('kompis-energy-efficiency-class', EnergyEfficiencyClass);
        this.customElements.define('kompis-water-efficiency-class', WaterEfficiencyClass);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-eprel-link', EprelLink);
        this.customElements.define('kompis-sec-link', SecLink);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.dexfSettings) || !!this.localizedInformation;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    /**
     * HTML for from price.
     *
     * @param totalPrice ...
     * @returns HTML for from price.
     */
    getFromPrice(totalPrice) {
        return html `
			<div class="fromPriceProductContent">
				${this.getProductName()}${this.getProductDescription()}
			</div>
			${totalPrice.map((price, index) => {
            const isPrimaryCurrency = index < 1;
            return this.getPrice(price, isPrimaryCurrency);
        })}
		`;
    }
    /**
     * HTML for PriceModule.
     * If "loyaltyProgramEqualFontSize" is present, we shift order of the commercial message.
     *
     * @param totalPrice ...
     * @returns HTML for price module.
     */
    getPriceModule(totalPrice) {
        const primaryCurrency = totalPrice[0];
        const loyaltyProgramEqualFontSize = this.getSettings().kompis.priceSettings.loyaltyProgramEqualFontSize ?? false;
        return html `
			${loyaltyProgramEqualFontSize ? null : this.getCommercialMessage(primaryCurrency)}
			${this.getProductContent()}
			${loyaltyProgramEqualFontSize ? this.getCommercialMessage(primaryCurrency) : null}
			${totalPrice.map((price, index) => {
            const isPrimaryCurrency = index < 1;
            return html ` ${this.getRegularPrice(price)} ${this.getPrice(price, isPrimaryCurrency)} `;
        })}
			${this.getText(totalPrice)} ${this.getEcoFee(primaryCurrency)} ${this.getRepairabilityIndex()}
			${this.getWaterEfficiencyClass()}
			${this.isHyperlinkComplianceType(this.shoppingProducts[0])
            ? this.getEnergyEfficiencyClass()
            : null}
		`;
    }
    /**
     * HTML for PriceModule but the price information is not available.
     *
     * @returns HTML for price module.
     */
    getPriceModuleWithoutPrice() {
        if (this.shoppingProducts.length !== 1) {
            const error = new ComponentElementException(this, 'There is more than one article and at least one of them does not contain price information which means that it is not possible to calculate a total price. This operation is not allowed.');
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        return html `
			${this.getProductContent()} ${this.getEnergyEfficiencyClass()} ${this.getText()}
			${this.getRepairabilityIndex()} ${this.getWaterEfficiencyClass()}
		`;
    }
    /**
     * Returns true if the compliance type is hyperlink (US energy/US lighting).
     *
     * @param shoppingProduct ...
     */
    isHyperlinkComplianceType(shoppingProduct) {
        return (ProductUtility.getProductComplianceType(shoppingProduct?.product) ===
            ProductComplianceTechnicalHeadingTypeEnum.hyperlink);
    }
    /**
     * Returns commercial message.
     *
     * @returns Template.
     * @param totalPrice Price model.
     */
    getCommercialMessage(totalPrice) {
        const commercialMessage = this.getCommercialMessageInput(totalPrice);
        if (!commercialMessage) {
            return null;
        }
        const { label, variant } = commercialMessage;
        return html `
			<skapa-commercial-message subtle variant="${variant}"> ${label} </skapa-commercial-message>
		`;
    }
    /**
     * Returns the commercial message label and type, or null if it should not be shown.
     *
     * @param totalPrice Price modal.
     */
    getCommercialMessageInput(totalPrice) {
        const translations = this.getTranslations();
        const { kompis: { priceSettings } } = this.getSettings();
        if (PriceUtility.isFamily(totalPrice)) {
            const family = 'family';
            return {
                label: translations.priceInfoIkeaFamilyPrice,
                variant: family
            };
        }
        const hasLowestPreviousSalesPrice = this.getLowestPreviousSalesPriceInclTax() !== null;
        const hasInformationForNewLowerPrice = PriceUtility.isNewLowerPriceWithPreviousPrice(totalPrice) || hasLowestPreviousSalesPrice;
        if (hasInformationForNewLowerPrice && priceSettings.showNewLowerPriceCommercialMessage) {
            const nlp = 'nlp';
            return {
                label: translations.productListNewLowerPrice,
                variant: nlp
            };
        }
        return null;
    }
    /**
     * This function wraps the name and description because of A11y, making it easier to hit the link to PIP.
     */
    getProductContent() {
        const productName = this.getProductName();
        if (!productName)
            return null;
        const productContent = document.createElement('div');
        productContent.className = 'productContent';
        productContent.appendChild(productName);
        productContent.appendChild(this.getProductDescription());
        if (this.showInteractiveProductName()) {
            productContent.addEventListener('click', this.onClick.bind(this));
            productContent.addEventListener('keydown', this.onKeyDown.bind(this));
        }
        return productContent;
    }
    showInteractiveProductName() {
        return PriceModuleUtility.showInteractiveProductName(this.productLink, this.disableLinks);
    }
    getProductName() {
        if (this.showInteractiveProductName()) {
            return this.getInteractiveProductName();
        }
        return this.getNonInteractiveProductName();
    }
    createProductNameHeading() {
        const element = document.createElement(this.disableHeadings ? 'p' : `h${this.startHeadingsAtLevel}`);
        element.id = 'productName';
        element.className = `productName notranslate ${SkapaStyle.Text.classnames(this.getProductNameClass())}`;
        return element;
    }
    getNonInteractiveProductName() {
        if (this.productName === null) {
            return null;
        }
        const headingElement = this.createProductNameHeading();
        headingElement.setAttribute('aria-labelledby', 'productName productDescription');
        headingElement.textContent = this.productName.toUpperCase();
        return headingElement;
    }
    createVisuallyHiddenLinkText(productName) {
        const { ariaLabelProductPageLink } = this.getTranslations();
        const element = document.createElement('span');
        element.className = 'visuallyHidden';
        element.innerText = ariaLabelProductPageLink
            .replace('[[productName]]', productName)
            .replace('{{productName}}', productName);
        return element;
    }
    createLinkInteractiveElement(productName) {
        const element = document.createElement('a');
        element.target = '_blank';
        element.rel = 'noopener noreferrer';
        element.href =
            ProductUtility.getProductInformationPageLink(this.shoppingProducts[0].product, this.getSettings()) ?? '';
        const productNameSpan = document.createElement('span');
        productNameSpan.textContent = productName.toUpperCase();
        productNameSpan.setAttribute('aria-hidden', 'true');
        element.appendChild(productNameSpan);
        element.appendChild(this.createVisuallyHiddenLinkText(productName));
        return element;
    }
    createButtonInteractiveElement(productName) {
        const element = document.createElement('button');
        element.textContent = productName.toUpperCase();
        return element;
    }
    createInteractiveElement(productName) {
        const isLink = this.productLink === PriceModuleProductLinkEnum.productInformationPage;
        const element = isLink
            ? this.createLinkInteractiveElement(productName)
            : this.createButtonInteractiveElement(productName);
        element.className = 'actionElement';
        element.setAttribute('aria-labelledby', 'productName productDescription');
        element.addEventListener('click', this.onProductNameClick.bind(this));
        return element;
    }
    getInteractiveProductName() {
        if (this.productName === null) {
            return null;
        }
        const headingElement = this.createProductNameHeading();
        const interactiveElement = this.createInteractiveElement(this.productName);
        headingElement.appendChild(interactiveElement);
        return headingElement;
    }
    getProductDescription() {
        const element = document.createElement('p');
        element.setAttribute('aria-hidden', 'true');
        element.className = `productDescription ${SkapaStyle.Text.classnames(this.getProductDescriptionClass())}`;
        element.id = 'productDescription';
        element.innerHTML = '<slot></slot>';
        return element;
    }
    /**
     * Get the regular price.
     *
     * @param totalPrice Price model.
     * @returns Regular price.
     */
    getRegularPrice(totalPrice) {
        if (!PriceUtility.isTro(totalPrice)) {
            return null;
        }
        return html `
			<kompis-text
				class="regularPrice"
				.theme="${this.theme}"
				.type="${this.getRegularPriceTextType()}"
				.bold="${true}"
			>
				<kompis-price-formatter
					.localizedInformation="${this.localizedInformation}"
					.settings="${this.settings}"
					.amount="${totalPrice.regularInclTax}"
					.strikeThrough="${true}"
					.ariaLabelPrice="${this.getTranslations().priceInfoRegularPrice}"
					.currencySymbol="${PriceUtility.getCurrencySymbol(totalPrice, this.getSettings())}"
				>
				</kompis-price-formatter>
			</kompis-text>
		`;
    }
    /**
     * Returns price.
     *
     * @param totalPrice Price model.
     * @param isPrimaryCurrency ...
     * @returns Template.
     */
    getPrice(totalPrice, isPrimaryCurrency) {
        const { priceInfoFromPrice, priceInfoCurrentPrice } = this.getTranslations();
        return html `
			<kompis-text
				class="${isPrimaryCurrency ? 'price' : 'secondaryPrice'}"
				.theme="${this.theme}"
				.bold="${true}"
				.type="${this.getPriceTextType(isPrimaryCurrency, PriceUtility.isTro(totalPrice))}"
			>
				<div class="priceContainer">
					${this.isFromPrice
            ? html `
								<span>${priceInfoFromPrice}</span>
								<div class="divider"></div>
							`
            : null}

					<kompis-price-formatter
						.localizedInformation="${this.localizedInformation}"
						.settings="${this.settings}"
						.amount="${this.isFromPrice
            ? PriceUtility.getFromPrice(totalPrice)
            : PriceUtility.getPrice(totalPrice)}"
						.mixedSize="${this.getPriceMixedSize(isPrimaryCurrency, PriceUtility.isTro(totalPrice))}"
						.animated="${this.animated}"
						.ariaLabelPrice="${priceInfoCurrentPrice}"
						.currencySymbol="${PriceUtility.getCurrencySymbol(totalPrice, this.getSettings())}"
					>
					</kompis-price-formatter>
					${this.getPriceUnit({ priceUnitLayout: 'emphasized' })}
				</div>

				${!this.isFromPrice &&
            isPrimaryCurrency &&
            !this.isHyperlinkComplianceType(this.shoppingProducts[0])
            ? this.getEnergyEfficiencyClass()
            : null}
			</kompis-text>
		`;
    }
    getPriceUnit(options) {
        if (!this.showPriceUnit) {
            return null;
        }
        if (!PriceUtility.isIndividualProduct(this.shoppingProducts)) {
            const error = new ComponentElementException(this, 'There are more than one product in combination with price unit, which is not allowed.');
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        const text = PriceUtility.getPriceUnit(this.shoppingProducts, this.getSettings().localisation.useMetricMeasures);
        if (!text) {
            return null;
        }
        return html `<span
			class="${options.priceUnitLayout === 'emphasized' ? 'priceUnitEmphasized' : ''}"
			>/${text}</span
		>`;
    }
    /**
     * Gets the energy class.
     *
     * @returns Template.
     */
    getEprelLink() {
        if (!this.showEnergyLabel) {
            return null;
        }
        if (this.shoppingProducts.length !== 1) {
            const error = new ComponentElementException(this, 'There are more than one product in combination with energy label, which is not allowed.');
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        const link = ProductUtility.getProductInformationSheetLink(this.shoppingProducts[0].product);
        if (link === null && !this.getFicheHeader(this.shoppingProducts[0].product)) {
            return null;
        }
        return html `
			<kompis-eprel-link
				.theme="${this.theme}"
				.product="${this.shoppingProducts[0].product}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.disableLinks="${this.disableLinks}"
				.visibleModal="${this.visibleModal === PriceModuleModalEnum.energyFicheCard
            ? EprelLinkModalEnum.energyFicheCard
            : null}"
				@modalOpen="${() => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: { visibleModal: PriceModuleModalEnum.energyFicheCard }
        }))}"
				@modalClose="${() => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }))}"
			>
			</kompis-eprel-link>
		`;
    }
    /**
     * Gets the water efficiency class.
     *
     * @returns Template.
     */
    getWaterEfficiencyClass() {
        if (!this.showEnergyLabel) {
            return null;
        }
        if (this.shoppingProducts.length !== 1) {
            const error = new ComponentElementException(this, 'There are more than one product in combination with water compliance, which is not allowed.');
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        return html `
			<kompis-water-efficiency-class
				.theme="${this.theme}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.visibleModal="${this.visibleModal && this.visibleModal in WaterEfficiencyClassModalEnum
            ? this.visibleModal
            : null}"
				.product="${this.shoppingProducts[0].product}"
				.disableLinks="${this.disableLinks}"
				@modalClose="${() => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }))}"
				@modalOpen="${() => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: { visibleModal: PriceModuleModalEnum.unifiedWaterLabelCard }
        }))}"
			>
			</kompis-water-efficiency-class>
		`;
    }
    /**
     * Gets the Chile SEC link.
     *
     * @returns Template.
     */
    getChileSecLink() {
        if (!this.showEnergyLabel) {
            return null;
        }
        if (this.shoppingProducts.length !== 1) {
            const error = new ComponentElementException(this, 'There are more than one product in combination with energy label, which is not allowed.');
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        const secLabels = this.shoppingProducts[0].product?.content?.complianceLabel?.filter((complianceLabelItem) => complianceLabelItem.labelType === ProductComplianceLabelTypeEnum.chileSecLabel);
        if (!secLabels || secLabels.length === 0) {
            return null;
        }
        return html `
			<kompis-sec-link
				.theme="${this.theme}"
				.product="${this.shoppingProducts[0].product}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.disableLinks="${this.disableLinks}"
				.visibleModal="${this.visibleModal === PriceModuleModalEnum.energySecCard
            ? ChileSecLinkModalEnum.energySecCard
            : null}"
				@modalOpen="${() => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: { visibleModal: PriceModuleModalEnum.energySecCard }
        }))}"
				@modalClose="${() => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }))}"
			>
			</kompis-sec-link>
		`;
    }
    /**
     * Return Fiche header if exists.
     *
     * @param product ...
     */
    getFicheHeader(product) {
        return (product?.content?.complianceTechnical?.find((info) => info.headingType === ProductComplianceTechnicalHeadingTypeEnum.nonRescaled)?.headingText);
    }
    /**
     * Gets the energy class.
     *
     * @returns Template.
     */
    getEnergyEfficiencyClass() {
        if (!this.showEnergyLabel) {
            return null;
        }
        if (this.shoppingProducts.length !== 1) {
            const error = new ComponentElementException(this, 'There are more than one product in combination with energy label, which is not allowed.');
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        return html `
			<kompis-energy-efficiency-class
				.theme="${this.theme}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.visibleModal="${this.visibleModal && this.visibleModal in EnergyEfficiencyClassModalEnum
            ? this.visibleModal
            : null}"
				.product="${this.shoppingProducts[0].product}"
				.disableLinks="${this.disableLinks}"
				@modalClose="${() => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }))}"
				@modalOpen="${() => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: { visibleModal: PriceModuleModalEnum.energyLabelCard }
        }))}"
			>
			</kompis-energy-efficiency-class>
		`;
    }
    /**
     * Returns text.
     *
     * @param totalPrice Price model.
     * @returns Template.
     */
    getText(totalPrice) {
        let elements = [];
        if (totalPrice) {
            const primaryCurrency = totalPrice[0];
            const { loyaltyProgramEqualFontSize } = this.getSettings().kompis.priceSettings;
            const taxAndPrice = [
                this.getPriceExclTax(primaryCurrency),
                this.getRegularPriceText(totalPrice)
            ];
            // Since regular price is displayed as a price instead of a price addon,
            // move it to the top.
            if (loyaltyProgramEqualFontSize) {
                taxAndPrice.reverse();
            }
            elements = [
                ...taxAndPrice,
                this.getPreviousPriceText(totalPrice),
                this.getLowestPreviousSalesPrice(totalPrice[0]),
                this.getValidInterval(primaryCurrency),
                this.getGeneralPriceDisclaimer()
            ];
        }
        elements.push([this.getEprelLink(), this.getChileSecLink()]);
        if (elements.some((element) => element !== null)) {
            return html `
				<kompis-text
					class="priceText"
					.theme="${this.theme}"
					.type="${ThemeFontStyleTypeEnum.bodySmall}"
				>
					${elements}
				</kompis-text>
			`;
        }
        return null;
    }
    getLowestPreviousSalesPriceInclTax() {
        if (!this.showLowestPreviousSalesPrice) {
            return null;
        }
        if (this.shoppingProducts.length !== 1) {
            const error = new ComponentElementException(this, 'There are more than one product in combination with lowest previous sales price, which is not allowed.');
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return null;
        }
        return (this.shoppingProducts[0].product.content?.priceInformation?.salesPrice
            .map((price) => price.lowestPreviousSalesPrice?.priceInclTax)
            .find((lowestPreviousSalesPriceInclTax) => !!lowestPreviousSalesPriceInclTax) ?? null);
    }
    getLowestPreviousSalesPrice(totalPrice) {
        const lowestPreviousSalesPriceInclTax = this.getLowestPreviousSalesPriceInclTax();
        if (lowestPreviousSalesPriceInclTax === null) {
            return null;
        }
        const { priceInfoLowestPreviousSalesPrice } = this.getTranslations();
        if (this.shoppingProducts.length) {
            return html `<div>
				<span aria-hidden="true">${priceInfoLowestPreviousSalesPrice}</span>
				<kompis-price-formatter
					class="lowestPreviousSalesPrice"
					.localizedInformation="${this.localizedInformation}"
					.settings="${this.settings}"
					.amount="${lowestPreviousSalesPriceInclTax}"
					.ariaLabelPrice="${priceInfoLowestPreviousSalesPrice}"
					.currencySymbol="${PriceUtility.getCurrencySymbol(totalPrice, this.getSettings())}"
				></kompis-price-formatter>
			</div>`;
        }
        return null;
    }
    /**
     * Returns price excl tax.
     *
     * @param totalPrice Price model.
     * @returns Template.
     */
    getPriceExclTax(totalPrice) {
        if (!this.showPriceExclTax) {
            return null;
        }
        const excludingTax = this.getTranslations().excludingTax;
        if (!this.getSettings().kompis.priceSettings.showPriceExcludingTax) {
            return null;
        }
        return html `
			<div class="priceExclTaxContainer">
				<kompis-price-formatter
					class="priceExclTax"
					.localizedInformation="${this.localizedInformation}"
					.settings="${this.settings}"
					.amount="${PriceUtility.getPriceExclTax(totalPrice)}"
					.ariaLabelPrice="${excludingTax}"
					.currencySymbol="${PriceUtility.getCurrencySymbol(totalPrice, this.getSettings())}"
				>
				</kompis-price-formatter>

				<span aria-hidden="true">${excludingTax}</span>
			</div>
		`;
    }
    /**
     * Returns regular price.
     * If "loyaltyProgramEqualFontSize" is true, the font size is the same as for the loyalty program price.
     *
     * @param totalPrice Price model.
     * @returns Template.
     */
    getRegularPriceText(totalPrice) {
        if (!PriceUtility.isFamily(totalPrice[0])) {
            return null;
        }
        const translations = this.getTranslations();
        if (this.getSettings().kompis.priceSettings.loyaltyProgramEqualFontSize) {
            return html `
				<kompis-text
					aria-hidden="true"
					class="secondaryPrice"
					.theme="${this.theme}"
					.bold="${true}"
					.type="${ThemeFontStyleTypeEnum.bodySmall}"
				>
					${translations.priceInfoRegularPrice}
				</kompis-text>
				<kompis-text
					class="price"
					.theme="${this.theme}"
					.bold="${true}"
					.type="${this.getPriceTextType(true)}"
				>
					<div class="priceContainer">
						<kompis-price-formatter
							.localizedInformation="${this.localizedInformation}"
							.settings="${this.settings}"
							.amount="${PriceUtility.getFromPrice(totalPrice[0])}"
							.mixedSize="${this.getPriceMixedSize(true, PriceUtility.isTro(totalPrice[0]))}"
							.animated="${this.animated}"
							.ariaLabelPrice="${translations.priceInfoRegularPrice}"
							.currencySymbol="${PriceUtility.getCurrencySymbol(totalPrice[0], this.getSettings())}"
						>
						</kompis-price-formatter>
						${this.getPriceUnit({ priceUnitLayout: 'emphasized' })}
					</div>
				</kompis-text>
			`;
        }
        return this.getPricesFromTotalPrice(totalPrice, translations.priceInfoRegularPrice);
    }
    /**
     * Returns previous price.
     *
     * @param totalPrice Price model.
     * @returns Template.
     */
    getPreviousPriceText(totalPrice) {
        const isNewLowerPriceAndFamilyPrice = PriceUtility.isNewLowerPriceWithPreviousPrice(totalPrice[0]) &&
            PriceUtility.isFamily(totalPrice[0]);
        if (isNewLowerPriceAndFamilyPrice ||
            !PriceUtility.isNewLowerPriceWithPreviousPrice(totalPrice[0])) {
            return null;
        }
        const priceInfoPreviousPrice = this.getTranslations().priceInfoPreviousPrice;
        return this.getPricesFromTotalPrice(totalPrice, priceInfoPreviousPrice);
    }
    /**
     * Returns valid interval.
     *
     * @param totalPrice Price model.
     * @returns Template.
     */
    getValidInterval(totalPrice) {
        const validInterval = PriceUtility.getValidInterval(totalPrice, undefined, this.getTranslations(), this.getSettings());
        return validInterval ? html `<div class="validInterval">${validInterval}</div>` : null;
    }
    /**
     * Returns general price disclaimer.
     *
     * @returns Template.
     */
    getGeneralPriceDisclaimer() {
        const { kompis: { priceSettings } } = this.getSettings();
        if (!priceSettings.showGeneralPriceDisclaimer) {
            return null;
        }
        return html `<div>${this.getTranslations().generalPriceDisclaimer}</div>`;
    }
    /**
     * Returns eco fee.
     *
     * @param totalPrice Price model.
     * @returns Template.
     */
    getEcoFee(totalPrice) {
        if (!this.showEcoFee || (!totalPrice.ecoPrice && !totalPrice.weeePrice)) {
            return null;
        }
        return html `
			<kompis-eco-fee
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.theme="${this.theme}"
				.totalPrice="${totalPrice}"
				.disableLinks="${this.disableLinks}"
				.visibleModal="${this.visibleModal ? this.visibleModal : null}"
				@modalClose="${(event) => {
            this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }));
            event.detail?.triggerElement?.focus();
        }}"
				@modalOpen="${() => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: { visibleModal: PriceModuleModalEnum.ecoFeeCard }
        }))}"
			>
			</kompis-eco-fee>
		`;
    }
    getRepairabilityIndex() {
        if (!this.showRepairabilityIndex) {
            return null;
        }
        if (this.shoppingProducts.length !== 1) {
            const error = new ComponentElementException(this, 'There are more than one product in combination with repairability index, which is not allowed.');
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        return html `
			<kompis-text .theme="${this.theme}" .type="${this.getPriceTextType(true)}">
				<kompis-repairability-index
					.localizedInformation="${this.localizedInformation}"
					.translations="${this.translations}"
					.theme="${this.theme}"
					.product="${this.shoppingProducts[0].product}"
					.disableLinks="${this.disableLinks}"
					.visibleModal="${this.visibleModal ? this.visibleModal : null}"
					@modalClose="${() => {
            this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }));
        }}"
					@modalOpen="${() => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: { visibleModal: PriceModuleModalEnum.repairabilityIndexCard }
        }))}"
				></kompis-repairability-index>
			</kompis-text>
		`;
    }
    onProductNameClick() {
        if (this.productLink !== PriceModuleProductLinkEnum.disabled) {
            this.dispatchEvent(new CustomEvent('productNameClick', { bubbles: true }));
        }
    }
    onKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            this.onClick(event);
        }
    }
    onClick(event) {
        const productNameActionElement = this.shadowRoot.querySelector('.actionElement');
        if (productNameActionElement !== event.target) {
            productNameActionElement?.click();
        }
    }
    /**
     * Returns product description Skapa class.
     *
     * @returns Class type.
     */
    getProductDescriptionClass() {
        switch (this.layout) {
            case PriceModuleLayoutTypeEnum.subtle:
                return { body: { s: true } };
            case PriceModuleLayoutTypeEnum.default:
            case PriceModuleLayoutTypeEnum.rowPrice:
            case PriceModuleLayoutTypeEnum.listPrice:
                return { body: { m: true } };
            case PriceModuleLayoutTypeEnum.emphasized:
                return { body: { l: true } };
        }
    }
    /**
     * Returns product name Skapa class.
     *
     * @returns Class type.
     */
    getProductNameClass() {
        switch (this.layout) {
            case PriceModuleLayoutTypeEnum.subtle:
                return { body: { s: true } };
            case PriceModuleLayoutTypeEnum.default:
            case PriceModuleLayoutTypeEnum.rowPrice:
            case PriceModuleLayoutTypeEnum.listPrice:
                return { heading: { xs: true } };
            case PriceModuleLayoutTypeEnum.emphasized:
                return { heading: { s: true } };
        }
    }
    /**
     * Returns price text type.
     *
     * @param isPrimaryCurrency ...
     * @param isTRO ...
     * @returns Text type.
     */
    getPriceTextType(isPrimaryCurrency, isTRO) {
        switch (this.layout) {
            case PriceModuleLayoutTypeEnum.subtle:
                return ThemeFontStyleTypeEnum.priceXSmall;
            case PriceModuleLayoutTypeEnum.default:
                return isPrimaryCurrency || isTRO
                    ? ThemeFontStyleTypeEnum.priceXLarge
                    : ThemeFontStyleTypeEnum.priceMedium;
            case PriceModuleLayoutTypeEnum.emphasized:
                return isPrimaryCurrency || isTRO
                    ? ThemeFontStyleTypeEnum.priceXXLarge
                    : ThemeFontStyleTypeEnum.priceLarge;
            case PriceModuleLayoutTypeEnum.rowPrice:
                return ThemeFontStyleTypeEnum.priceSmall;
            case PriceModuleLayoutTypeEnum.listPrice:
                return isPrimaryCurrency || isTRO
                    ? ThemeFontStyleTypeEnum.priceLarge
                    : ThemeFontStyleTypeEnum.priceSmall;
        }
    }
    /**
     * Returns if price is mixed size.
     *
     * @param isPrimaryCurrency ...
     * @param isTRO ...
     * @returns Mixed size.
     */
    getPriceMixedSize(isPrimaryCurrency, isTRO) {
        switch (this.layout) {
            case PriceModuleLayoutTypeEnum.subtle:
            case PriceModuleLayoutTypeEnum.rowPrice:
                return false;
            case PriceModuleLayoutTypeEnum.listPrice:
                if (!isPrimaryCurrency && !isTRO) {
                    return false;
                }
        }
        return true;
    }
    /**
     * Returns regular price text type.
     *
     * @returns Text type.
     */
    getRegularPriceTextType() {
        switch (this.layout) {
            case PriceModuleLayoutTypeEnum.subtle:
            case PriceModuleLayoutTypeEnum.default:
            case PriceModuleLayoutTypeEnum.rowPrice:
            case PriceModuleLayoutTypeEnum.listPrice:
                return ThemeFontStyleTypeEnum.priceXSmall;
            case PriceModuleLayoutTypeEnum.emphasized:
                return ThemeFontStyleTypeEnum.priceSmall;
        }
    }
}
__decorate([
    property({ type: Object })
], PriceModule.prototype, "shoppingProducts", void 0);
__decorate([
    property({ type: Object })
], PriceModule.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], PriceModule.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], PriceModule.prototype, "settings", void 0);
__decorate([
    property({ type: String })
], PriceModule.prototype, "layout", void 0);
__decorate([
    property({ type: String })
], PriceModule.prototype, "productName", void 0);
__decorate([
    property({ type: Object, required: true })
], PriceModule.prototype, "theme", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "underline", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "animated", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "disableLinks", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "disableFocusStyle", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "showEcoFee", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "showPriceUnit", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "showEnergyLabel", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "showRepairabilityIndex", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "showPriceExclTax", void 0);
__decorate([
    property({ type: String })
], PriceModule.prototype, "visibleModal", void 0);
__decorate([
    property({ type: String })
], PriceModule.prototype, "productLink", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "isFromPrice", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "isCombinedProduct", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "showLowestPreviousSalesPrice", void 0);
__decorate([
    property({ type: Number })
], PriceModule.prototype, "startHeadingsAtLevel", void 0);
__decorate([
    property({ type: Boolean })
], PriceModule.prototype, "disableHeadings", void 0);
//# sourceMappingURL=PriceModule.js.map